import { useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Eye, FileText, Mail, MessageSquare, PenLine, RefreshCcw, Wallet, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/PortalPage";

const TONE = {
  customer_signed: ["#4CCC06", PenLine], customer_accepted: ["#4CCC06", CheckCircle2], change_order_accepted: ["#4CCC06", CheckCircle2],
  payment_verified: ["#4CCC06", Wallet], paid: ["#4CCC06", Wallet], partially_paid: ["#9BE15D", Wallet], payment_recorded: ["#C9C9C9", Wallet],
  customer_viewed: ["#60A5FA", Eye], internal_preview: ["#777", Eye], email_open_signal: ["#777", Mail], email_sent: ["#C9C9C9", Mail],
  delivery_failed: ["#EF4444", AlertTriangle], customer_declined: ["#EF4444", XCircle], change_order_rejected: ["#EF4444", XCircle],
  overdue: ["#F59E0B", AlertTriangle], changes_requested: ["#F59E0B", RefreshCcw], reapproval_required: ["#F59E0B", RefreshCcw],
  internal_conversion: ["#A78BFA", RefreshCcw], admin_override: ["#A78BFA", AlertTriangle], refunded: ["#F59E0B", Wallet],
  sms_sent: ["#C9C9C9", MessageSquare], delivery_confirmed: ["#4CCC06", CheckCircle2], reminder_sent: ["#60A5FA", Mail],
  sms_opt_in: ["#4CCC06", MessageSquare], sms_opt_out: ["#EF4444", MessageSquare],
};
const KIND = { quote: "Quote", invoice: "Invoice", job: "Job", change_order: "Change order", customer: "Customer" };
const SKIP = new Set(["accepted_scope", "accepted_terms", "drawn_path", "user_agent", "changes"]);
const when = (s) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const show = (v) => (v && typeof v === "object" ? JSON.stringify(v).slice(0, 140) : String(v).length > 64 && /^[a-f0-9]{40,}$/.test(String(v)) ? `${String(v).slice(0, 12)}…` : String(v));

const Who = ({ a }) => {
  if (!a || a.type === "system") return <span className="text-[#888]">ALR Ops (automatic)</span>;
  const tag = a.type === "customer" ? "customer · link holder, not a login" : a.type === "alr_admin" ? "ALR admin" : a.verified ? "signed-in staff" : "staff (unverified)";
  return <span>{a.name || (a.type === "customer" ? "Customer" : "Staff")} <span className="text-[#777]">({tag})</span></span>;
};

const Changes = ({ c }) => !c ? null : (
  <p className="mt-1 text-[11px] text-[#A0A0A0]">
    {c.added?.length > 0 && <>Added: {c.added.join(", ")}. </>}{c.removed?.length > 0 && <>Removed: {c.removed.join(", ")}. </>}
    {c.changed?.length > 0 && <>Changed: {c.changed.map((x) => `${x.item} (${x.from} → ${x.to})`).join(", ")}. </>}
    {c.total_from !== c.total_to && <>Total {c.total_from} → {c.total_to}. </>}{c.terms_changed && <>Terms changed. </>}
  </p>
);

export const EventRow = ({ e }) => {
  const [color, Icon] = TONE[e.type] || ["#C9C9C9", FileText];
  const ev = e.evidence || {};
  const keys = Object.keys(ev).filter((k) => !SKIP.has(k) && ev[k] !== null && ev[k] !== "" && ev[k] !== undefined);
  return (
    <li className="relative pl-7" data-testid={`activity-event-${e.type}`}>
      <span className="absolute left-0 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-[#333] bg-[#0E0E0E]"><Icon size={11} color={color} /></span>
      <p className="text-[12px] font-bold" style={{ color }}>{e.label}</p>
      <p className="text-[11px] text-[#C9C9C9]">{when(e.at)} · <Who a={e.actor} /></p>
      <p className="text-[11px] text-[#888]">{KIND[e.document?.kind] || "Record"}{e.document?.number ? ` #${e.document.number}` : ""}{e.revision > 1 ? ` · version ${e.revision}` : ""} · {e.channel} · {e.source}</p>
      {e.note && <p className="mt-0.5 text-[11px] italic text-[#C9C9C9]">“{e.note}”</p>}
      <Changes c={ev.changes} />
      {keys.length > 0 && (
        <details className="mt-1 text-[11px] text-[#888]" data-testid={`activity-evidence-${e.id}`}>
          <summary className="cursor-pointer text-[#A0A0A0]">Evidence</summary>
          <div className="mt-1 space-y-0.5 rounded-lg bg-[#0E0E0E] p-2">{keys.map((k) => <p key={k}><span className="text-[#666]">{k.replace(/_/g, " ")}:</span> {show(ev[k])}</p>)}</div>
        </details>
      )}
    </li>
  );
};

export const ActivityTimeline = ({ scope, id, reloadKey, testId = "activity-timeline" }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { if (id) api(`/billing/activity?scope=${scope}&id=${id}`).then(setRows).catch((e) => setErr(e.message)); }, [scope, id, reloadKey]);
  return (
    <Card className="mt-4" data-testid={testId}>
      <p className="mb-1 flex items-center gap-2 text-xs font-extrabold tracking-[1.5px] text-neutral-400"><Activity size={14} />ACTIVITY TIMELINE</p>
      <p className="mb-3 text-[11px] text-[#777]">Every event with who did it and the proof behind it. Email-open signals are hints only. Staff previews never count as customer views.</p>
      {err && <p className="text-xs text-[#ff8080]">{err}</p>}
      {!rows && !err && <p className="text-xs text-[#777]">Loading…</p>}
      {rows && !rows.length && <p className="text-xs text-[#777]" data-testid={`${testId}-empty`}>No activity recorded yet. Tracking started Oct 27, 2026.</p>}
      {rows?.length > 0 && <ol className="space-y-3 border-l border-[#262626] pl-0" data-testid={`${testId}-list`}>{rows.map((e) => <EventRow key={e.id} e={e} />)}</ol>}
    </Card>
  );
};
